/**
 * Pure helpers for the robot telemetry tab: calendar-day math in the viewer's
 * timezone, display formatting, and the comparisons the summary tiles show.
 * No React, no network — everything here is unit-tested directly.
 */

export const FALLBACK_TZ = "Asia/Kolkata";
export const RANGE_OPTIONS = [7, 14, 30];
export const DEFAULT_RANGE = 14;
// How many earlier days the summary compares the selected day against.
export const BASELINE_DAYS = 7;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const pad = (n) => String(n).padStart(2, "0");

// Chrome still reports India's pre-2008 zone name; send the current one.
const ZONE_ALIASES = { "Asia/Calcutta": "Asia/Kolkata" };

/** The viewer's IANA timezone, or IST when the browser won't say. */
export const viewerTimeZone = () => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TZ;
    return ZONE_ALIASES[zone] || zone;
  } catch {
    return FALLBACK_TZ;
  }
};

/** "India Standard Time" for Asia/Kolkata; the IANA name if there's no long form. */
export const timeZoneLabel = (tz) => {
  try {
    const part = new Intl.DateTimeFormat("en-IN", { timeZone: tz, timeZoneName: "long" })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName");
    return part?.value || tz;
  } catch {
    return tz;
  }
};

/** YYYY-MM-DD for a Date in the browser's local timezone. */
export const toDateStr = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const isDateStr = (value) => DATE_RE.test(String(value || ""));

/** Local-midnight Date for a YYYY-MM-DD string. */
export const fromDateStr = (str) => {
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export const todayStr = (now = new Date()) => toDateStr(now);

/** Shift a YYYY-MM-DD string by whole days (calendar-safe across DST). */
export const shiftDate = (str, days) => {
  const d = fromDateStr(str);
  d.setDate(d.getDate() + days);
  return toDateStr(d);
};

/** The `days`-long window ending on `endStr`, inclusive. */
export const rangeEnding = (endStr, days) => ({ from: shiftDate(endStr, -(days - 1)), to: endStr });

export const parseRange = (value) => {
  const n = Number(value);
  return RANGE_OPTIONS.includes(n) ? n : DEFAULT_RANGE;
};

/** A requested day, clamped to [today - 89, today]; anything else is today. */
export const parseDay = (value, today) => {
  if (!isDateStr(value) || value > today || value < shiftDate(today, -89)) return today;
  return value;
};

// ── Formatting ──────────────────────────────────────────────────────────────

export const DASH = "—";
const isNum = (n) => typeof n === "number" && Number.isFinite(n);

const numberFmt = (digits) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits, minimumFractionDigits: 0 });

export const formatCount = (n) => (isNum(n) ? numberFmt(0).format(n) : DASH);

/** 0.929 -> "92.9%". */
export const formatRate = (rate) => (isNum(rate) ? `${numberFmt(1).format(rate * 100)}%` : DASH);

/** Battery percentage points -> "61%" / "1.5%". */
export const formatPct = (n, digits = 1) => (isNum(n) ? `${numberFmt(digits).format(n)}%` : DASH);

/**
 * Battery drawn over many runs, which can pass 100% when the robot recharges
 * during the day: "53%" below a full charge, "1.3 charges" from one upward.
 */
export const formatCharge = (pct) => {
  if (!isNum(pct)) return DASH;
  if (pct < 100) return `${numberFmt(0).format(pct)}%`;
  const charges = Math.round(pct / 10) / 10;
  return `${numberFmt(1).format(charges)} ${charges === 1 ? "charge" : "charges"}`;
};

/** Metres -> "640 m" under a kilometre, "18.4 km" above. */
export const formatDistance = (m) => {
  if (!isNum(m)) return DASH;
  if (m < 1000) return `${numberFmt(0).format(m)} m`;
  return `${numberFmt(m < 10000 ? 2 : 1).format(m / 1000)} km`;
};

export const formatSpeed = (mps) => (isNum(mps) ? `${numberFmt(2).format(mps)} m/s` : DASH);

/** Seconds -> "3h 12m", "14m", "45s". */
export const formatDuration = (sec) => {
  if (!isNum(sec)) return DASH;
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (!h) return `${m}m`;
  return `${h}h ${pad(m)}m`;
};

const dayFmt = new Intl.DateTimeFormat("en-IN", {
  weekday: "short",
  day: "numeric",
  month: "short",
});
const longDayFmt = new Intl.DateTimeFormat("en-IN", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});
const timeFmt = (tz) =>
  new Intl.DateTimeFormat("en-IN", { hour: "numeric", minute: "2-digit", timeZone: tz });

/** "Sat, 11 Oct". */
export const formatDay = (str) => dayFmt.format(fromDateStr(str));

/** "Saturday, 11 October 2026" — for screen readers. */
export const formatDayLong = (str) => longDayFmt.format(fromDateStr(str));

/** A run's start time on the robot's day, in the viewer's timezone: "2:05 pm". */
export const formatTime = (iso, tz) => {
  if (!iso) return DASH;
  try {
    return timeFmt(tz).format(new Date(iso));
  } catch {
    return timeFmt(FALLBACK_TZ).format(new Date(iso));
  }
};

/** Title for the summary: "Today so far", "Yesterday", or the date. */
export const dayTitle = (str, today) => {
  if (str === today) return "Today so far";
  if (str === shiftDate(today, -1)) return "Yesterday";
  return formatDay(str);
};

/** NO_VALID_PATH -> "No valid path". */
export const reasonLabel = (code) => {
  if (!code) return "Unknown";
  const words = String(code).toLowerCase().replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const OUTCOME_LABELS = { SUCCEEDED: "Succeeded", ABORTED: "Aborted", CANCELED: "Canceled" };

/** One sentence per day for the bar's accessible name. */
export const describeDay = (day, today) => {
  const when = day.date === today ? `Today, ${formatDayLong(day.date)}` : formatDayLong(day.date);
  if (!day.runs) return `${when}: no runs`;
  const parts = [`${day.succeeded} succeeded`];
  if (day.aborted) parts.push(`${day.aborted} aborted`);
  if (day.canceled) parts.push(`${day.canceled} canceled`);
  return `${when}: ${day.runs} ${day.runs === 1 ? "run" : "runs"}, ${parts.join(", ")}`;
};

// ── Comparisons ─────────────────────────────────────────────────────────────

/**
 * Average of `key` over the BASELINE_DAYS days before `date` that had runs.
 * Days without runs are skipped so an idle weekend doesn't drag the average
 * down; null when there's nothing to compare against.
 */
export const baselineAverage = (days, date, key) => {
  const prior = days
    .filter((d) => d.date < date && d.runs > 0 && isNum(d[key]))
    .slice(-BASELINE_DAYS);
  if (!prior.length) return null;
  return prior.reduce((sum, d) => sum + d[key], 0) / prior.length;
};

/**
 * Signed change against the baseline, with whether that's good news.
 * `higherIsBetter` false flips the tone (battery drain going up is bad).
 */
export const compare = (value, baseline, { higherIsBetter = true, epsilon = 1e-9 } = {}) => {
  if (!isNum(value) || !isNum(baseline)) return null;
  const delta = value - baseline;
  if (Math.abs(delta) < epsilon) return { delta: 0, direction: "same", tone: "neutral" };
  const up = delta > 0;
  return {
    delta,
    direction: up ? "up" : "down",
    tone: up === higherIsBetter ? "good" : "bad",
  };
};

/** Largest bar in the strip — the y-scale. Never 0, so an empty range draws flat. */
export const maxRuns = (days) => Math.max(1, ...days.map((d) => d.runs || 0));

/** Every Nth label on a long strip, always keeping the selected day and today. */
export const showDayLabel = (index, count, date, selected, today) => {
  if (count <= 14 || date === selected || date === today) return true;
  return (count - 1 - index) % 5 === 0;
};
